import React from 'react';
import { usePermitRequestContext } from '../../../../contexts/PermitRequestContext';
import { useProjectConfigContext } from '../../../../contexts/ProjectConfigContext';
import SectionTitle from '../SectionTitle';
import FormSection from './FormSection';
import RadioBtnsContainer from '../../../../components/form/radios/RadioBtnsContainer';
import RadioOutlineBtn from '../../../../components/form/radios/RadioOutlineBtn';
import InputGroup from '../../../../components/form/InputGroup';
import addRequestPageLang from '../../../../data/lang/add-request-page.lang';
import { useTags } from '../../../../hooks/useTags';
import formErrorsLang from '../../../../data/lang/form-errors.lang';

function AdditionalAddressFormSection() {
    const { projectConfigState } = useProjectConfigContext();
    const { handleChange, Formik } = usePermitRequestContext();

    const { errors, values, touched, handleBlur, setFieldValue } = Formik;

    const hasAdditionalAddress = values.hasAdditionalAddress === 1 ? true : false;
    const AdditionalAddrDetails = values.additionalContactDetails;
    const AdditionalAddrDetailsErrors = errors.additionalContactDetails ? errors.additionalContactDetails : {};
    const AdditionalAddrDetailsTouched = touched.additionalContactDetails ? touched.additionalContactDetails : {};

    const generateTag = useTags();
    const title = generateTag(addRequestPageLang, 'page-title');
    // Define variables for Hebrew tags
    const sectionTitle = generateTag(addRequestPageLang, 'form.additional-address-section.title');
    const additionalAddressLabel = generateTag(addRequestPageLang, 'form.additional-address-section.additionalAddressLabel');
    const yesLabel = generateTag(addRequestPageLang, 'form.additional-address-section.yesLabel');
    const noLabel = generateTag(addRequestPageLang, 'form.additional-address-section.noLabel');
    const cityLabel = generateTag(addRequestPageLang, 'form.additional-address-section.cityLabel');
    const streetLabel = generateTag(addRequestPageLang, 'form.additional-address-section.streetLabel');
    const houseLabel = generateTag(addRequestPageLang, 'form.additional-address-section.houseLabel');
    const apartmentLabel = generateTag(addRequestPageLang, 'form.additional-address-section.apartmentLabel');
    const postalCodeLabel = generateTag(addRequestPageLang, 'form.additional-address-section.postalCodeLabel');

    const mailCityTouched = AdditionalAddrDetailsTouched.mailCity;
    const mailCityError = AdditionalAddrDetailsErrors.mailCity;
    const mailCityMsg = generateTag(formErrorsLang, mailCityError && mailCityTouched ? mailCityError : '');

    const mailStreetTouched = AdditionalAddrDetailsTouched.mailStreet;
    const mailStreetError = AdditionalAddrDetailsErrors.mailStreet;
    const mailStreetMsg = generateTag(formErrorsLang, mailStreetError && mailStreetTouched ? mailStreetError : '');

    const mailHouseTouched = AdditionalAddrDetailsTouched.mailHouse;
    const mailHouseError = AdditionalAddrDetailsErrors.mailHouse;
    const mailHouseMsg = generateTag(formErrorsLang, mailHouseError && mailHouseTouched ? mailHouseError : '');

    const mailApartmentTouched = AdditionalAddrDetailsTouched.mailAppartment;
    const mailApartmentError = AdditionalAddrDetailsErrors.mailAppartment;
    const mailApartmentMsg = generateTag(formErrorsLang, mailApartmentError && mailApartmentTouched ? mailApartmentError : '');

    const mailMikodTouched = AdditionalAddrDetailsTouched.mailMikod;
    const mailMikodError = AdditionalAddrDetailsErrors.mailMikod;
    const mailMikodMsg = generateTag(formErrorsLang, mailMikodError && mailMikodTouched ? mailMikodError : '');

    const extraFilesConfig = projectConfigState.addRequestPageConfig

    if (!extraFilesConfig.hasAdditionalAddress) {
        return null;
    }

    return (
        <FormSection>
            <SectionTitle title={sectionTitle} />
            <div className='row'>
                <div className='col-sm'>
                    <RadioBtnsContainer label={additionalAddressLabel}>
                        <RadioOutlineBtn
                            isChecked={hasAdditionalAddress}
                            label={yesLabel}
                            value={1}
                            onClick={handleClick}
                            id="additionalAddressYes"
                            name='hasAdditionalAddress'
                        />
                        <RadioOutlineBtn
                            isChecked={!hasAdditionalAddress}
                            label={noLabel}
                            value={0}
                            onClick={handleClick}
                            id="additionalAddressNo"
                            name='hasAdditionalAddress'
                        />
                    </RadioBtnsContainer>
                </div>
                {hasAdditionalAddress ? (
                    <>
                        <div className='row'>
                            <div className='col-md-6 col-lg-3'>
                                <InputGroup
                                    name={'additionalContactDetails.mailCity'}
                                    label={cityLabel}
                                    value={AdditionalAddrDetails.mailCity}
                                    handleBlur={handleBlur}
                                    handleChange={handleChange}
                                    isRequired={true}
                                    errorMsg={mailCityMsg}
                                />
                            </div>
                            <div className='col-md-3 col-lg-3'>
                                <InputGroup
                                    name={'additionalContactDetails.mailStreet'}
                                    label={streetLabel}
                                    value={AdditionalAddrDetails.mailStreet}
                                    handleBlur={handleBlur}
                                    handleChange={handleChange}
                                    isRequired={true}
                                    errorMsg={mailStreetMsg}
                                />
                            </div>
                            <div className='col-md-6 col-lg-2'>
                                <InputGroup
                                    name={'additionalContactDetails.mailHouse'}
                                    label={houseLabel}
                                    value={AdditionalAddrDetails.mailHouse}
                                    handleBlur={handleBlur}
                                    handleChange={handleChange}
                                    isRequired={true}
                                    errorMsg={mailHouseMsg}
                                />
                            </div>
                        </div>
                        <div className='row'>
                            <div className='col-md-6 col-lg-2'>
                                <InputGroup
                                    name={'additionalContactDetails.mailAppartment'}
                                    label={apartmentLabel}
                                    value={AdditionalAddrDetails.mailAppartment}
                                    handleBlur={handleBlur}
                                    handleChange={handleChange}
                                    isRequired={true}
                                    errorMsg={
                                        mailApartmentMsg
                                    }
                                />
                            </div>
                            <div className='col-md-6 col-lg-2'>
                                <InputGroup
                                    name={'additionalContactDetails.mailMikod'}
                                    label={postalCodeLabel}
                                    value={AdditionalAddrDetails.mailMikod}
                                    handleBlur={handleBlur}
                                    handleChange={handleChange}
                                    isRequired={true}
                                    errorMsg={mailMikodMsg}
                                />
                            </div>
                        </div>
                    </>
                ) : (
                    ''
                )}
            </div>
        </FormSection>
    );

    function handleClick(e: React.MouseEvent<HTMLInputElement, MouseEvent>) {
        setFieldValue('hasAdditionalAddress', Number(e.currentTarget.value));
    }
}

export default AdditionalAddressFormSection;